import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { EventData, RoomControlCard, RoomControlCardAction, RoomControlCardRef, UserCallData } from "@kalyzee/escaet-ui";
import { isMeetingSessionRoleGreaterThanOrEqual, useTimeEventsOfTheDay } from "@kalyzee/kast-web-sdk";
import { MeetingContainer } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MeetingOutputDTO, MeetingSessionRole, MeetingState, RoomOutputDTO, TimeEventTargetModels } from "@kalyzee/kast-web-sdk/http";
import { delay, GridList, useRefAutoUpdate, useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingJoinRequest } from "@kalyzee/kast-web-sdk/websocket";
import { PromiseQueue } from "../../helpers/promise";
import { sdkClient } from "../../helpers/sdk";
import { useMultiMeetingAPI, useMultiMeetingSelector } from "../../hooks/meeting";

import { getEscaetUserCallData, joinMeetingFromPedagogy } from "../../helpers/meeting";
import { usePedagogyContext } from "../../pages/master/pedagogy/context";
import MeetingSessionMediaPlayer from "../player/MeetingSessionMediaPlayer";
import { ErrorView } from "../utils/ErrorView";
import { LoadingView } from "../utils/LoadingView";
import { convertTimeEvent } from "../utils/TimeEventsAgenda";
import styles from "./RoomsControlCard.module.css";
import { expertWebcamName } from "../../helpers/webcam";

export type RoomsControlCardData = { room: RoomOutputDTO; meeting?: Meeting }[];
export interface RoomsControlCardProps {
  onRooms?: (rooms: RoomOutputDTO[]) => void;
  onMode?: (room: RoomOutputDTO, mode: "agenda" | "current-event") => void;
  onWaitingRoom?: (container: MeetingContainer, meeting: Meeting) => void;
  onTalkToExpert?: (container: MeetingContainer, meeting: Meeting) => void;
  onJoinMeeting?: (container: MeetingContainer, meeting: Meeting) => void;
  onSupervise?: (container: MeetingContainer, meeting: Meeting) => void;
  onSearchUsers?: (container: MeetingContainer, meeting: Meeting, users: UserCallData[]) => void;
  onCurrentMeetings?: (data: RoomsControlCardData) => void;
  onJoinRequest?: (container: MeetingContainer, meetingId: string) => Omit<MeetingJoinRequest, "id"> | undefined;
  onData?: (data: RoomsControlCardData) => void;
  kind?: "pedagogy" | "preview";
  style?: React.CSSProperties;
  className?: string;
}

export interface RoomsControlCardRef {
  setMode: (mode: "agenda" | "current-event") => void;
  allRoomsAreInAgendaMode: () => boolean;
  getData: () => { room: RoomOutputDTO; meeting?: Meeting }[] | undefined;
}

export const RoomsControlCard = React.forwardRef(
  (
    {
      onRooms,
      onMode,
      onWaitingRoom,
      onTalkToExpert,
      onJoinMeeting,
      onSupervise,
      onSearchUsers,
      onCurrentMeetings,
      onJoinRequest,
      onData,
      kind = "pedagogy",
      style,
      className,
    }: RoomsControlCardProps,
    forwardRef: React.ForwardedRef<RoomsControlCardRef>
  ) => {
    const divRef = useRef<HTMLDivElement>(null);

    const multiMeetingAPI = useMultiMeetingAPI();
    const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);
    const meetingContainersAPI = useMemo(() => multiMeetingAPI.all?.getContainers()?.map((c) => c.getAPI()) ?? [], [multiMeetingAPI]);
    const localContainersRef = useRef<MeetingContainer[]>([]);

    const [date, setDate, dateRef] = useStateWithRef(new Date());
    const [meetings] = useMultiMeetingSelector(meetingContainersAPI, (state) => state.meeting);
    const [rooms, setRooms] = useState<RoomOutputDTO[]>();
    const [error, setError] = useState<"rooms">();
    const { timeEvents, error: timeEventError, retry } = useTimeEventsOfTheDay(sdkClient, date, undefined, false, { targets: [TimeEventTargetModels.Meeting], populate: { actor: { identity: true } } });
    const [meetingIdsByRoom, setMeetingIdsByRoom] = useStateWithRef<{ [roomId: string]: Meeting["id"] | undefined }>({});
    const [queueRef] = useState({ current: new PromiseQueue() });
    const roomControlCardRefs = useRef<RoomControlCardRef[]>([]);

    const pedagogyCtx = usePedagogyContext();
    const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);
    // Grid data
    const gridData = useMemo(() => rooms ? [...rooms] : [], [rooms, timeEvents, meetings, meetingIdsByRoom]); // refresh

    const classes = useMemo(() => {
      const classes: string[] = [styles.container];
      if (className) classes.push(className);
      return classes.join(" ");
    }, [className]);

    const onRoomsRef = useRefAutoUpdate(onRooms);
    const onCurrentMeetingsRef = useRefAutoUpdate(onCurrentMeetings);
    const onDataRef = useRefAutoUpdate(onData);

    const getData = useCallback(() => {
      if (!rooms) return undefined;
      const result: { room: RoomOutputDTO; meeting?: Meeting }[] = [];
      rooms?.forEach((r) => {
        const currentMeetingId = meetingIdsByRoom[r._id];
        const currentMeeting = meetings?.find((m) => m?.id === currentMeetingId);
        result.push({ room: r, meeting: currentMeeting });
      });
      return result;
    }, [rooms, meetingIdsByRoom, meetings]);

    useImperativeHandle(
      forwardRef,
      () => ({
        setMode: (mode) => {
          roomControlCardRefs.current.forEach((r) => {
            r.setMode(mode);
          });
        },
        allRoomsAreInAgendaMode: () => {
          let result = true;
          roomControlCardRefs.current.forEach((r) => {
            result = result && r.getMode() === "agenda";
          });
          return result;
        },
        getData,
      }),
      [getData]
    );

    // ---------------------------------------------------------------------- //
    // ---------------------------   UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const reloadRooms = () => {
      sdkClient
        .getHttpClient()
        .room.getAll({})
        .then((r) => {
          setError(undefined);
          setRooms(r.data);
        })
        .catch((err) => {
          setError("rooms");
        });
    };

    const updateEvents = useCallback(async () => {
      const currentMeetingIdsByRoom: typeof meetingIdsByRoom = {};
      const meetingIds: Set<string> = new Set();
      rooms?.forEach((r) => {
        let meetingId: string | undefined = undefined;
        const runningMeetingsForThisRoom =
          timeEvents?.filter((t) => {
            if (t._room !== r._id) return false;
            if (t.targetModel !== TimeEventTargetModels.Meeting) return false;
            const meeting = t.target as MeetingOutputDTO;
            if (meeting.state !== MeetingState.RUNNING && meeting.state !== MeetingState.PAUSED) return false;
            return true;
          }) ?? [];
        const now = new Date().getTime();
        runningMeetingsForThisRoom.sort((a, b) => {
          const startA = a.startDatetime ? new Date(a.startDatetime).getTime() : 0;
          const startB = b.startDatetime ? new Date(b.startDatetime).getTime() : 0;
          return Math.abs(startA - now) - Math.abs(startB - now);
        });
        if (runningMeetingsForThisRoom.length) meetingId = runningMeetingsForThisRoom[0]._target;
        currentMeetingIdsByRoom[r._id] = meetingId;
        if (meetingId) meetingIds.add(meetingId);
      });

      const containers = [...localContainersRef.current];
      for (let c of containers) {
        if (c.getStore().getState().meeting?.specializedData?.isPedagogy) continue;
        const meetingId = c.getMeetingId();
        if (!meetingIds.has(meetingId)) {
          localContainersRef.current = localContainersRef.current.filter((curr) => curr !== c);
          c.destroy();
        }
      }

      const meetingIdsArray = Array.from(meetingIds);
      const unscheduleDestroyContainer = pedagogyCtxRef.current?.containers.filter((c) => meetingIdsArray.includes(c.getMeetingId())) ?? [];
      pedagogyCtxRef.current?.unscheduleDestroyContainer(unscheduleDestroyContainer);

      const meetingsToJoin: { container: MeetingContainer; id: string }[] = [];

      const containerAPI = multiMeetingAPIRef.current.all;
      if (!containerAPI) return;
      meetingIds.forEach((id) => {
        const container = containerAPI.getOrCreateContainer(id);
        if (!container) return; // can't join
        if (container.getStatus() !== "disconnected") {
          if (!localContainersRef.current.find((curr) => curr === container)) {
            localContainersRef.current.push(container);
          }
          return; // already joined
        }
        meetingsToJoin.push({ container, id });
      });

      if (meetingsToJoin) await delay(200);
      for (let meeting of meetingsToJoin) {
        const container = await joinMeetingFromPedagogy(containerAPI, meeting.id, pedagogyCtxRef.current, onJoinRequest?.(meeting.container, meeting.id))
        if (container) localContainersRef.current.push(meeting.container);
      }
      setMeetingIdsByRoom(currentMeetingIdsByRoom);
    }, [rooms, timeEvents]);

    // ---------------------------------------------------------------------- //
    // ---------------------------   EFFECT ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      reloadRooms();
      const interval = setInterval(() => {
        const now = new Date();
        if (now.getDate() !== dateRef.current.getDate()) {
          setDate(now);
        }
      }, 60000); // check date every minute
      return () => clearInterval(interval);
    }, []);

    useEffect(() => {
      queueRef.current.add(() => updateEvents());
    }, [updateEvents]);

    useEffect(() => {
      if (rooms) onRoomsRef.current?.(rooms);
    }, [rooms]);

    useEffect(() => {
      const data = getData();
      if (data) {
        onDataRef.current?.(data);
        onCurrentMeetingsRef.current?.(data);
      } 
    }, [getData]);


    // ---------------------------------------------------------------------- //
    // ---------------------------   RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (error || timeEventError) return <ErrorView retry={() => retry()} />;
    if (timeEvents === undefined) return <LoadingView />;

    const renderRoomCard = (room?: RoomOutputDTO) => {
      if (!room) return null;
      const events: EventData[] = (timeEvents
        ?.filter((t) => t._room === room._id)
        ?.map((t) => convertTimeEvent(t))
        ?.filter(Boolean) ?? []) as EventData[];
      const currentMeetingId = meetingIdsByRoom[room._id];
      const meeting = meetings.find((m) => m?.id === currentMeetingId);
      const onlineExpertSessions = meeting?.sessions?.filter((s) => isMeetingSessionRoleGreaterThanOrEqual(s.role, MeetingSessionRole.MODERATOR)) ?? [];
      const anOnlineExpertNeedsHelp = onlineExpertSessions.some((s) => meeting?.usersData?.some(u => u.userId === s.user?.id && u.sosMode));
      const container = multiMeetingAPIRef.current.all?.getContainers()?.find((c) => c.getMeetingId() === meeting?.id);
      const currentTimeEvent = meeting ? timeEvents?.find((t) => t.target._id === meeting.id) : undefined;
      const currentEvent = currentTimeEvent ? convertTimeEvent(currentTimeEvent) : undefined;
      if (currentEvent && meeting) {
        currentEvent.students = getEscaetUserCallData(meeting);
        currentEvent.eventStartedAt = meeting.startedAt ? new Date(meeting.startedAt) : undefined;
      }
      const experts = meeting?.sessions?.filter((s) => isMeetingSessionRoleGreaterThanOrEqual(s.role, MeetingSessionRole.MODERATOR)) ?? [];
      const renderPlayers = () => {
        if (!meeting) return [];
        const sessions =
          container
            ?.getServices()
            .receptionManager.getOvenMediaSessions()
            ?.filter((s) => experts.find((e) => e.id === s.data.session.id))
            ?.filter((s) => s.data.mediaSession.name !== expertWebcamName) ?? [];

        if (!sessions.length) {
          return [
            {
              key: "no_session",
              node: <div className={styles.noSession}>Aucun flux</div>,
            },
          ];
        }
        return sessions.map((s) => ({
          key: s.getId(),
          node: (
            <MeetingSessionMediaPlayer
              className={styles.playerContainer}
              muted
              autoOnOffVideo
              meeting={meeting}
              sessionId={s.data.session.id}
              mediaSessionId={s.data.mediaSession.id}
              ovenMediaSession={s}
            />
          ),
        }));
      };
      let currentRef: RoomControlCardRef | null = null;
      return (
        <RoomControlCard
          ref={(r: RoomControlCardRef) => {
            if (currentRef) roomControlCardRefs.current = roomControlCardRefs.current.filter((r) => r !== currentRef);
            if (r) roomControlCardRefs.current.push(r);
            currentRef = r;
          }}
          title={`SALLE ${room.name}`}
          subtitle={currentEvent?.title}
          events={events}
          currentEvent={currentEvent}
          players={renderPlayers()}
          kind={kind}
          sos={anOnlineExpertNeedsHelp}
          pause={meeting?.state === MeetingState.PAUSED}
          onAction={(action: RoomControlCardAction, event: EventData) => {
            if (!container || !meeting) return;
            if (meeting.state === MeetingState.PAUSED) {
              if (action === "enter_the_waiting_room") return;
              if (action === "talk_to_expert") return;
              if (action === "join") return;
            }
            if (action === "search" && event.students) onSearchUsers?.(container, meeting, event.students);
            else if (action === "enter_the_waiting_room") onWaitingRoom?.(container, meeting);
            else if (action === "talk_to_expert") onTalkToExpert?.(container, meeting);
            else if (action === "join") onJoinMeeting?.(container, meeting);
            else if (action === "supervise") onSupervise?.(container, meeting);
          }}
          onMode={(mode: "agenda" | "current-event") => {
            onMode?.(room, mode);
          }}
          style={{ width: "100%", height: "100%" }}
        ></RoomControlCard>
      );
    };

    return (
      <div className={classes} ref={divRef} style={style}>
        <GridList
          style={{ width: "100%", height: "100%" }}
          targetContentRatio={620 / 480}
          minRow={1}
          minColumn={1}
          maxRow={2}
          maxColumn={2}
          data={gridData}
          renderItem={(_, room) => renderRoomCard(room)}
          gap={20}
          resizeObserverRef={divRef}
          noPages
        ></GridList>
      </div>
    );
  }
);
